import React, { useRef, useState, useEffect } from 'react';
import {
  Animated, StyleSheet, Platform,
} from 'react-native';
import { Transitioning, Transition } from 'react-native-reanimated';

import DraggableLongPressGestureHandler from '../gesture-handlers/DraggableLongPressGestureHandler';
import DraggablePanGestureHandler from '../gesture-handlers/DraggablePanGestureHandler';
import { DraggableFlatListContext, defaults } from '../contexts/DraggableFlatListContext';
import { Provider as ScrollViewContextProvider } from '../contexts/ScrollViewContext';
import { Provider as DragAndDropContextProvider } from '../contexts/DragAndDropContext';
import { Provider as AutoScrollProvider } from '../contexts/AutoScrollContext';
import DraggedItem from './DraggedItem';
import FlatListComponent from './FlatListComponent';
import { usePrevious } from '../utils';


const transition = (
  <Transition.Sequence>
    <Transition.In type='fade' propagation='top' />
    <Transition.Out type='fade' propagation='top' />
    <Transition.Change interpolation='easeInOut' propagation='top' />
  </Transition.Sequence>
);

const ROOT_REF = React.createRef();

function easeInOut() {
  ROOT_REF && ROOT_REF.current && ROOT_REF.current.animateNextTransition();
}

const styles = StyleSheet.create({
  flexOne: { flex: 1 },
});

const DraggableFlatList = (props) => (
  <Animated.View style={styles.flexOne}>
    <DraggablePanGestureHandler>
      <Animated.View style={styles.flexOne}>
        <DraggableLongPressGestureHandler>
          <Animated.View style={styles.flexOne}>
            <FlatListComponent
              {...props}
            />
            <DraggedItem />
          </Animated.View>
        </DraggableLongPressGestureHandler>
      </Animated.View>
    </DraggablePanGestureHandler>
  </Animated.View>
);


const DraggableFlatListWrapper = ({
  data,
  onReorder,
  itemHeight,
  waitForLongPressMs,
  keyExtractor,
  renderItem,
  headerComponentOffset,
  onDropHorizontal,
  backgroundColor,
  snapToAnimationDuration,
  ...rest
}) => {
  const longPressGestureRef = React.createRef(),
        scrollViewGestureRef = React.createRef(),
        verticalPanGestureRef = React.createRef(),
        previousData = usePrevious(data);

  const relativeY = useRef(new Animated.Value(0)),
        translateX = useRef(new Animated.Value(0)),
        scrollContentOffsetY = useRef(new Animated.Value(0)),
        scrollViewComponent = useRef(null),
        onPanGestureEvent = useRef(Animated.event([{
          nativeEvent: {
            y: relativeY.current,
            translationX: translateX.current,
          },
        }],
        {
          useNativeDriver: true,
        }));


  useEffect(() => {
    if (previousData && data.length !== previousData.length) {
      easeInOut();
      // setAnimKey(uuid());
    }
  }, [data.length, previousData]);

  const style = Platform.OS === 'web' ? {
    top: window.location.href.indexOf('later') > -1 ? 70 : 0, left: 0, right: 0, backgroundColor,
  } : { backgroundColor };

  const Inner = (
    <Animated.View style={[
      { opacity: Animated.add(scrollContentOffsetY.current, new Animated.Value(10000)) },
      styles.flexOne,
      style,
    ]}
    >
      <DraggableFlatListContext.Provider value={{
        backgroundColor,
        data,
        itemHeight,
        keyExtractor,
        longPressGestureRef,
        scrollContentOffsetY,
        onPanGestureEvent: onPanGestureEvent.current,
        onDropHorizontal,
        headerComponentOffset,
        onReorder,
        relativeY: relativeY.current,
        renderItem,
        scrollViewComponent,
        scrollViewGestureRef,
        snapToAnimationDuration,
        translateX: translateX.current,
        verticalPanGestureRef,
        waitForLongPressMs,
      }}
      >
        <ScrollViewContextProvider>
          <DragAndDropContextProvider>
            <AutoScrollProvider>
              <DraggableFlatList {...rest} />
            </AutoScrollProvider>
          </DragAndDropContextProvider>
        </ScrollViewContextProvider>
      </DraggableFlatListContext.Provider>
    </Animated.View>
  );

  return Platform.OS === 'android' ? Inner
    : (
      <Transitioning.View
        style={styles.flexOne}
        transition={transition}
        ref={ROOT_REF}
      >
        { Inner }
      </Transitioning.View>
    );
};

DraggableFlatListWrapper.defaultProps = defaults;

export default React.memo(DraggableFlatListWrapper);
